import React, { useState, useEffect, useCallback } from 'react';
import { addDays, format } from 'date-fns';
import ErrorsFilters from './ErrorsFilters';
import ErrorsSummary from './ErrorsSummary';
import ErrorsChart from './ErrorsChart';
import ErrorsTable from './ErrorsTable';

// Вспомогательная функция для получения номера месяца из строки
const getMonthNumber = (monthStr) => {
  const months = {
    'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6,
    'Jul': 7, 'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12
  };
  return months[monthStr] || 1;
};

// Глобальная переменная для функции экспорта CSV
window.exportErrorsToCSV = () => {
  console.log('Export function not initialized');
};

const ErrorsApp = ({ domains: initialDomainsData, landings: initialLandingData }) => {
  // Состояния
  const [domains, setDomains] = useState(initialDomainsData || []);
  const [landings, setLandings] = useState(initialLandingData || []);
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [selectedLandings, setSelectedLandings] = useState(['all']);
  const [selectedCountry, setSelectedCountry] = useState({ value: 'All', label: 'All Countries' });
  const [landingOptions, setLandingOptions] = useState([]);
  const [sortedErrors, setSortedErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorCategories, setErrorCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [selectedTrafficSource, setSelectedTrafficSource] = useState({ value: 'all', label: 'All Sources' });
  const [selectedDateRange, setSelectedDateRange] = useState([
    {
      startDate: addDays(new Date(), -7),
      endDate: new Date(),
      key: 'selection',
    },
  ]);
  
  // По умолчанию период display - 'day' для графика
  const [selectedPeriod, setSelectedPeriod] = useState('day');
  
  // Стейты для трансформированных данных
  const [transformedData, setTransformedData] = useState({ 
    errorTypes: [], 
    dailyData: {}, 
    errorMessages: {},
    dailyAggregatedData: {}
  });
  
  // Функция трансформации данных ошибок
  const transformErrorData = useCallback((data) => {
    const errorTypes = new Set();
    const dailyData = {};
    const errorMessages = {};
  
    // Сортируем записи по возрастанию даты
    const sortedEntries = Object.entries(data).sort((a, b) => {
      const [dayA, monthA, yearA] = a[0].split(' ');
      const [dayB, monthB, yearB] = b[0].split(' ');
      const dateA = new Date(`${yearA}-${getMonthNumber(monthA)}-${dayA}`);
      const dateB = new Date(`${yearB}-${getMonthNumber(monthB)}-${dayB}`);
      return dateA - dateB;
    });
  
    sortedEntries.forEach(([timestamp, errors]) => {
      const [day, month, year, time] = timestamp.split(' ');
      const formattedDate = `${year}-${String(getMonthNumber(month)).padStart(2, '0')}-${day.padStart(2, '0')} ${time}`;
      
      if (!dailyData[formattedDate]) {
        dailyData[formattedDate] = { total: 0 };
      }
  
      errors.forEach(error => {
        // Используем категорию как есть
        const categoryName = error.error_category;
        
        errorTypes.add(categoryName);
        
        if (!dailyData[formattedDate][categoryName]) {
          dailyData[formattedDate][categoryName] = 0;
        }
        
        if (!errorMessages[categoryName]) {
          errorMessages[categoryName] = new Set();
        }
        
        // Добавляем сообщения об ошибках
        if (error.error_messages && Array.isArray(error.error_messages)) {
          error.error_messages.forEach(msg => {
            errorMessages[categoryName].add(msg);
          });
        } else if (error.error_message) {
          errorMessages[categoryName].add(error.error_message);
        }
        
        dailyData[formattedDate][categoryName] += error.error_count || 0;
        dailyData[formattedDate].total += error.error_count || 0;
      });
    });
  
    // Создаем отсортированный объект dailyData
    const sortedDailyData = Object.fromEntries(
      Object.entries(dailyData).sort((a, b) => new Date(a[0]) - new Date(b[0]))
    );
    
    const errorTypesArray = Array.from(errorTypes);
    const errorMessagesObject = Object.fromEntries(
      Object.entries(errorMessages).map(([category, messages]) => [
        category,
        Array.from(messages)
      ])
    );
    
    // Группируем данные по дням для таблицы и экспорта
    const dailyAggregatedData = {};
    
    Object.entries(sortedDailyData).forEach(([timestamp, counts]) => {
      const [datePart] = timestamp.split(' ');
      
      if (!dailyAggregatedData[datePart]) {
        dailyAggregatedData[datePart] = {
          total: 0,
          ...errorTypesArray.reduce((acc, type) => ({ ...acc, [type]: 0 }), {})
        };
      }
      
      // Суммируем значения за день
      errorTypesArray.forEach(errorType => {
        dailyAggregatedData[datePart][errorType] += counts[errorType] || 0;
      });
      dailyAggregatedData[datePart].total += counts.total;
    });
  
    return {
      errorTypes: errorTypesArray,
      dailyData: sortedDailyData,
      errorMessages: errorMessagesObject,
      dailyAggregatedData
    };
  }, []);
  
  // Настраиваем функцию экспорта в CSV
  useEffect(() => {
    if (transformedData.errorTypes.length > 0 && Object.keys(transformedData.dailyAggregatedData).length > 0) {
      window.exportErrorsToCSV = () => {
        // Заголовки
        const headers = ['Date', ...transformedData.errorTypes, 'Total'];
        
        // Преобразуем данные
        const rows = Object.entries(transformedData.dailyAggregatedData).map(([date, counts]) => {
          const [year, month, day] = date.split('-');
          const formattedDate = `${day}.${month}.${year}`;
          
          return [
            formattedDate,
            ...transformedData.errorTypes.map(type => counts[type] || 0),
            counts.total
          ];
        });

        // Добавляем строку с тотальными значениями
        const totalRow = [
          'Total',
          ...transformedData.errorTypes.map(type => 
            Object.values(transformedData.dailyAggregatedData).reduce((sum, counts) => sum + (counts[type] || 0), 0)
          ),
          Object.values(transformedData.dailyAggregatedData).reduce((sum, counts) => sum + counts.total, 0)
        ];

        // Собираем все строки
        const allRows = [headers, ...rows, totalRow];
        
        // Конвертируем в CSV
        const csv = allRows.map(row => row.join(',')).join('\n');
        
        // Создаем и скачиваем файл
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        link.setAttribute('href', url);
        link.setAttribute('download', 
          `errors_${format(selectedDateRange[0].startDate, 'dd-MM-yyyy')}_${format(selectedDateRange[0].endDate, 'dd-MM-yyyy')}.csv`
        );
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      };
    }
  }, [transformedData, selectedDateRange]);
  
  // Загрузка категорий ошибок
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch('/api/error_categories');
        if (response.ok) {
          const data = await response.json();
          setErrorCategories(data.error_categories || []);
        }
      } catch (error) {
        console.error('Error fetching error categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };
    
    fetchCategories();
  }, []);

  // Обновление опций лендингов при изменении домена
  useEffect(() => {
    if (selectedDomain === 'all') {
      setLandingOptions([{ id: 'all', name: 'All Landings' }]);
    } else {
      const filteredLandings = landings.filter(
        landing => landing.domain_id === parseInt(selectedDomain)
      );
      setLandingOptions([
        { id: 'all', name: 'All Landings' },
        ...filteredLandings
      ]);
    }
  }, [selectedDomain, landings]);

  // Загрузка данных при первом рендере
  useEffect(() => {
    handleFormSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Обработка данных после обновления sortedErrors
  useEffect(() => {
    if (Object.keys(sortedErrors).length > 0) {
      const newTransformedData = transformErrorData(sortedErrors);
      // Сортируем типы ошибок по алфавиту, чтобы обеспечить постоянный порядок
      newTransformedData.errorTypes.sort((a, b) => a.localeCompare(b));
      setTransformedData(newTransformedData);
    }
  }, [sortedErrors, transformErrorData]);

  // Функция отправки запроса для получения данных
  const handleFormSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
  
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content;
      
      if (!token) {
        throw new Error('CSRF token not found');
      }
  
      // Получаем исходные даты из выбранного диапазона
      const startDateObj = selectedDateRange[0].startDate;
      const endDateObj = selectedDateRange[0].endDate;
  
      // Создаем даты в UTC независимо от локальной временной зоны
      const startDate = new Date(Date.UTC(
        startDateObj.getFullYear(),
        startDateObj.getMonth(),
        startDateObj.getDate(),
        0, 0, 0, 0
      ));
      
      const endDate = new Date(Date.UTC(
        endDateObj.getFullYear(),
        endDateObj.getMonth(),
        endDateObj.getDate(),
        23, 59, 59, 999
      ));
  
      const response = await fetch('/api/errors/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
        },
        body: JSON.stringify({
          selectedDomain,
          selectedLandings,
          selectedCountry: selectedCountry.value,
          dateRange: [{
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString()
          }],
          ...(selectedTrafficSource.value !== 'all' && { traffic_source_id: selectedTrafficSource.value })
        }),
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const data = await response.json();
      setSortedErrors(data.errors || {});
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load error data. Please try again.');
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  // Обработчик изменения периода отображения для графика
  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  // Если загрузка данных
  if (isLoading || loadingCategories) {
    return <div className="flex justify-center items-center p-5 text-gray-600">Loading error data...</div>;
  }

  // Если ошибка
  if (error) {
    return <div className="p-4 text-red-700 bg-red-100 rounded-md">{error}</div>;
  }

  const { errorTypes, dailyData, errorMessages, dailyAggregatedData } = transformedData;

  return (
    <div className="wrapper">
      {/* Компонент фильтров */}
      <ErrorsFilters 
        domains={domains}
        landings={landings}
        selectedDomain={selectedDomain}
        selectedLandings={selectedLandings}
        selectedCountry={selectedCountry}
        selectedDateRange={selectedDateRange}
        selectedTrafficSource={selectedTrafficSource}
        setSelectedDomain={setSelectedDomain}
        setSelectedLandings={setSelectedLandings}
        setSelectedCountry={setSelectedCountry}
        setSelectedDateRange={setSelectedDateRange}
        setSelectedTrafficSource={setSelectedTrafficSource}
        landingOptions={landingOptions}
        handleFormSubmit={handleFormSubmit}
        isSubmitting={isSubmitting}
        setDomains={setDomains}
        setLandings={setLandings}
      />

      <div className="p-2">
        {/* Таблица и график */}
        {Object.keys(dailyData).length > 0 ? (
          <>
            {/* График */}
            <div className="mt-4 mb-4">
              <ErrorsChart 
                data={dailyData} 
                errorTypes={errorTypes}
                selectedPeriod={selectedPeriod}
                onPeriodChange={handlePeriodChange}
              />
            </div>
            
            {/* Блок статистики - теперь под графиком */}
            <ErrorsSummary 
              errorTypes={errorTypes}
              dailyAggregatedData={dailyAggregatedData}
            />
            
            {/* Таблица */}
            <ErrorsTable 
              errorTypes={errorTypes}
              dailyAggregatedData={dailyAggregatedData}
              errorMessages={errorMessages}
            />
          </>
        ) : (
          <div className="text-center p-5 text-gray-600 bg-gray-50 rounded-md mb-2">
            No errors found for the selected period
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorsApp;